/* eslint-disable no-unused-vars, react/jsx-indent */
import _ from 'underscore';
import React from 'react';
import { LayoutContext } from './layout-context';
import { layoutConfigs } from './layout-configs';
import { Mode } from './mode';
import { withLayoutContext } from './with-layout-context';
import { ILayoutConfig, IChangeLayoutFn } from './types';

export interface ILayoutHoCBaseProps<P = unknown> {
  componentProps: P;
  changeLayout: IChangeLayoutFn;
  component: React.ComponentType<P>;
  toLayout: ILayoutConfig;
  isGridLayout: boolean;
  layoutConfig: ILayoutConfig;
}

export type IBasicLayoutHoC = <P = unknown>(Component: React.ComponentType<P>) => React.FC<P>;

class LayoutHoCBase<P> extends React.Component<ILayoutHoCBaseProps<P>> {
  constructor(props: ILayoutHoCBaseProps<P>) {
    super(props);
    props.changeLayout(this.getTargetLayout(props.toLayout), true);
  }

  shouldComponentUpdate(nextProps: ILayoutHoCBaseProps<P>) {
    const { component, componentProps } = nextProps;
    return !_.isEqual(component, this.props.component) || !_.isEqual(componentProps, this.props.componentProps);
  }

  componentDidUpdate(prevProps: ILayoutHoCBaseProps<P>) {
    if (!_.isEqual(this.props.component, prevProps.component) || !_.isEqual(this.props.toLayout, prevProps.toLayout)) {
      this.props.changeLayout(this.getTargetLayout(this.props.toLayout), true);
    }
  }

  getTargetLayout(toLayout: ILayoutConfig): ILayoutConfig {
    if (!this.props.isGridLayout) {
      return toLayout;
    }

    const { layoutConfig } = this.props;

    if (layoutConfig && layoutConfig.menu && layoutConfig.menu.mode === Mode.Collapsed) {
    // if menu is collapsed (by user interaction), maintain the collapsed state
      return { ...toLayout, menu: { mode: Mode.Collapsed } };
    }

    return toLayout;
  }

  render() {
    const { componentProps, component: Component } = this.props;
    return <Component changeLayout={this.props.changeLayout} {...componentProps} />;
  }
}

const LayoutHoC = withLayoutContext(LayoutHoCBase);

export function withFullScreenLayout<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change
    }) => (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreen} changeLayout={change} componentProps={props} />)}
                        </LayoutContext.Consumer>);
}

export function withLayout<P = unknown>(Component: React.ComponentType<P>, toLayout: ILayoutConfig): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change
    }) => (<LayoutHoC component={Component} toLayout={toLayout} changeLayout={change} componentProps={props} />)}
                        </LayoutContext.Consumer>);
}

export function withMenuLayout<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      config
    }) => (<Component {...props} {...config.menu} />)}
                        </LayoutContext.Consumer>);
}

export function withDefaultLayout<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change
    }) => (<LayoutHoC component={Component} toLayout={{}} changeLayout={change} componentProps={props} />)}
                        </LayoutContext.Consumer>);
}

export function withCollapsedMenuOnly<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change
    }) => (<LayoutHoC component={Component} toLayout={layoutConfigs.withCollapsedMenuOnly} changeLayout={change} componentProps={props} />)}
                        </LayoutContext.Consumer>);
}

export function withCollapsedMenuAndNav<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change
    }) => (<LayoutHoC component={Component} toLayout={layoutConfigs.withCollapsedMenuAndNav} changeLayout={change} componentProps={props} />)}
                        </LayoutContext.Consumer>);
}

export function withLayoutNavRefresh<P = unknown>(Component: React.ComponentType<P>, toLayout: ILayoutConfig): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (<LayoutHoC component={Component} toLayout={{ ...toLayout, nav: { mode: Mode.Expanded }, menu: { mode: Mode.Expanded } }} changeLayout={change} componentProps={props} />);
      }

      return (<LayoutHoC component={Component} toLayout={toLayout} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}

export function withLayoutNavRefreshNoMenu<P = unknown>(Component: React.ComponentType<P>, toLayout: ILayoutConfig): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (<LayoutHoC component={Component} toLayout={{ ...toLayout, nav: { mode: Mode.Expanded } }} changeLayout={change} componentProps={props} />);
      }

      return (<LayoutHoC component={Component} toLayout={toLayout} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}

export function withLayoutNavRefreshWithTab<P = unknown>(Component: React.ComponentType<P>, toLayout: ILayoutConfig): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        let { tab } = toLayout;
        if (tab.mode === Mode.Hidden) {
          tab = {
            ...toLayout.tab,
            mode: Mode.Collapsed,
            showDatePicker: false,
            singleTabMode: true,
          };
        }

        return (
          <LayoutHoC
            component={Component}
            toLayout={{
              ...toLayout,
              nav: { mode: Mode.Expanded },
              menu: { mode: Mode.Expanded },
              tab,
            }}
            changeLayout={change}
            componentProps={props}
          />
        );
      }

      return (<LayoutHoC component={Component} toLayout={toLayout} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}

export const withMenuOnlyLayout: IBasicLayoutHoC = _.partial(withLayoutNavRefresh, _, layoutConfigs.withMenuOnly);
export const withoutMenuLayout: IBasicLayoutHoC = _.partial(withLayoutNavRefresh, _, layoutConfigs.withoutMenu);
export const withSingleTabLayout: IBasicLayoutHoC = _.partial(withLayoutNavRefresh, _, layoutConfigs.withSingleTab);
export const withSummaryBarAndTabNoDateLayout: IBasicLayoutHoC = _.partial(withLayout, _, { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withoutDate });
export const withSummaryBarAndTabAndSubTabLayout: IBasicLayoutHoC = _.partial(withLayout, _, { ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withTabAndSubTab });
export const withFullScreenWithSummaryBar: IBasicLayoutHoC = _.partial(withLayout, _, layoutConfigs.fullScreenWithSummaryBar);
export const withFullScreenWithSummaryBarAndTabLayout: IBasicLayoutHoC = _.partial(withLayout, _, { ...layoutConfigs.fullScreenWithSummaryBarAndTab });
export const withMessageBarLayout: IBasicLayoutHoC = _.partial(withLayout, _, layoutConfigs.withMessageBar);

// *NavRefresh no L1, L2 in original shell, while adding L1, L2 for nav refresh

export function withFullScreenLayoutNavRefresh<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithNavRefresh} changeLayout={change} componentProps={props} />);
      }

      return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreen} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}

export function withOnboardingLayout<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (
          <LayoutHoC
            component={Component}
            toLayout={{
              ...layoutConfigs.fullScreenWithNavRefresh,
              ...layoutConfigs.withoutNav,
              status: { mode: Mode.Hidden },
              tab: { mode: Mode.Hidden },
            }}
            changeLayout={change}
            componentProps={props}
          />
        );
      }

      return (
        <LayoutHoC
          component={Component}
          toLayout={{
            ...layoutConfigs.fullScreen,
            ...layoutConfigs.withoutNav,
            status: { mode: Mode.Hidden },
            tab: { mode: Mode.Hidden },
          }}
          changeLayout={change}
          componentProps={props}
        />
      );
    }}
                        </LayoutContext.Consumer>);
}

export function withTabNoDateLayoutNavRefresh<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.staticComponents, ...layoutConfigs.withoutDate, ...layoutConfigs.withoutStatus }} changeLayout={change} componentProps={props} />);
      }

      return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.fullScreenWithTab, ...layoutConfigs.withoutDate }} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}

export function withSummaryBarAndTabNoDateLayoutNavRefresh<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.staticComponents, ...layoutConfigs.withoutDate }} changeLayout={change} componentProps={props} />);
      }

      return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withoutDate }} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}

export function withSummaryBarAndTabAndSubTabLayoutNavRefresh<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.staticComponents, ...layoutConfigs.withTabAndSubTab }} changeLayout={change} componentProps={props} />);
      }

      return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.fullScreenWithSummaryBarAndTab, ...layoutConfigs.withTabAndSubTab }} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}

export function withFullScreenWithSummaryBarNavRefresh<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithSummaryBarWithNavRefresh} changeLayout={change} componentProps={props} />);
      }

      return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithSummaryBar} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}

export function withFullScreenWithSummaryBarNavRefreshNoMenu<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (<LayoutHoC component={Component} toLayout={{ ...layoutConfigs.fullScreenWithSummaryBar, nav: { mode: Mode.Expanded } }} changeLayout={change} componentProps={props} />);
      }

      return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithSummaryBar} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}

export function withFullScreenWithSummaryBarAndTabLayoutNavRefresh<P = unknown>(Component: React.ComponentType<P>): React.FC<P> {
  return (props: P) => (<LayoutContext.Consumer>
    {({
      change,
      isGridLayout
    }) => {
      if (isGridLayout) {
        return (<LayoutHoC component={Component} toLayout={layoutConfigs.staticComponents} changeLayout={change} componentProps={props} />);
      }

      return (<LayoutHoC component={Component} toLayout={layoutConfigs.fullScreenWithSummaryBarAndTab} changeLayout={change} componentProps={props} />);
    }}
                        </LayoutContext.Consumer>);
}
